/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid util/print.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { DataGridItem } from '../types'

// Printing a virtualized grid straight from the DOM would print the twenty
// rows that happen to be in the window, and a paginated one would print the
// current page. Neither is what anyone means by "print this table", so the
// grid renders a plain, complete table into the document, prints that, and
// throws it away. The print stylesheet hides everything else on the page.

const CLASS_NAME_PRINT_ROOT = 'data-grid-print-root'

export interface PrintColumn {
  id: string
  label: string
  formatter?: (value: unknown, item: DataGridItem) => string
}

export interface PrintRow {
  getValue: (columnId: string) => unknown
  original: DataGridItem
}

export interface PrintTable {
  // The rows BEFORE pagination: the whole result of the user's sorting and
  // filtering, which is the table they think they are printing.
  getSortedRowModel?: () => { rows: PrintRow[] }
  getFilteredRowModel?: () => { rows: PrintRow[] }
  getCoreRowModel: () => { rows: PrintRow[] }
}

export interface PrintOptions {
  // Printed above the table. Omitted entirely when not set.
  title?: string
}

// The widest row model the registered features expose — feature sets without
// sorting or filtering fall back to the core one.
const printRows = (table: PrintTable): PrintRow[] => {
  if (typeof table.getSortedRowModel === 'function') {
    return table.getSortedRowModel().rows
  }

  if (typeof table.getFilteredRowModel === 'function') {
    return table.getFilteredRowModel().rows
  }

  return table.getCoreRowModel().rows
}

export const createPrintTable = (
  table: PrintTable,
  columns: PrintColumn[],
  options: PrintOptions = {}
): HTMLElement => {
  const root = document.createElement('div')
  root.className = CLASS_NAME_PRINT_ROOT

  if (options.title) {
    const heading = document.createElement('h1')
    heading.textContent = options.title
    root.append(heading)
  }

  const tableElement = document.createElement('table')
  tableElement.classList.add('table')

  const head = document.createElement('thead')
  const headRow = document.createElement('tr')
  for (const column of columns) {
    const cell = document.createElement('th')
    cell.scope = 'col'
    cell.textContent = column.label
    headRow.append(cell)
  }

  head.append(headRow)

  const body = document.createElement('tbody')
  for (const row of printRows(table)) {
    const rowElement = document.createElement('tr')
    for (const column of columns) {
      const cell = document.createElement('td')
      const value = row.getValue(column.id)
      // textContent, never innerHTML: a custom cell renderer's markup is not
      // reachable here, and the printed page must not become an injection
      // surface for the data.
      cell.textContent = column.formatter ? column.formatter(value, row.original) : String(value ?? '')
      rowElement.append(cell)
    }

    body.append(rowElement)
  }

  tableElement.append(head, body)
  root.append(tableElement)

  return root
}

export const printGrid = (table: PrintTable, columns: PrintColumn[], options: PrintOptions = {}): void => {
  const root = createPrintTable(table, columns, options)
  document.body.append(root)

  // afterprint fires on every browser we support; the finally is the safety
  // net for a print dialog that never opens (a headless or blocked context),
  // where leaving the clone in the document would be worse than a flicker.
  const cleanup = () => {
    root.remove()
    window.removeEventListener('afterprint', cleanup)
  }

  window.addEventListener('afterprint', cleanup)

  try {
    window.print()
  } finally {
    cleanup()
  }
}
